import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../service';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Grid,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  makeStyles,
  Avatar,
  CircularProgress,
} from '@material-ui/core';
import {
  getJobByID,
  killJobById,
  killJobWithFeeById,
} from 'src/redux/jobs/action';
import { getVehicleByID } from 'src/redux/vehicles/action';
import SingleLineImageList from './SingleLineImageList';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    flex: '1 0 auto',
  },
  cover: {
    width: 151,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  large: {
    width: `${theme.spacing(18)} !important`,
    height: `${theme.spacing(18)} !important`,
  },
  playIcon: {
    height: 38,
    width: 38,
  },
  detailButton: {
    position: 'absolute !important',
    right: 5,
    bottom: 5,
  },
}));

const EditJob = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const classes = useStyles();
  const [job, setJob] = useState(null);
  const [vehicle, setVehicle] = useState();
  // Plan v4.7: chat log between this order's client and washer
  const [messages, setMessages] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState('');

  const loadMessages = async () => {
    setMessagesLoading(true);
    setMessagesError('');
    try {
      const { data } = await api.get(`/backoffice/order/${params.jobId}/messages`);
      setChatMessages((data && data.data && data.data.chat) || []);
      setMessages((data && data.data && (data.data.system || data.data.messages)) || []);
    } catch (e) {
      setMessagesError(e?.response?.data?.codeDetail || 'failed_to_load_messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  useEffect(() => {
    if (params.jobId) loadMessages();
  }, [params.jobId]);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      dispatch(getJobByID(params.jobId)).then((res) => {
        setJob(res);
        const vehicleID = res && res.order && res.order[0] && res.order[0].vehicle_id;
        if (!vehicleID) {
          // no_washer / refund stubs may have no vehicle attached.
          // Skip the vehicle fetch instead of crashing on undefined[0].id.
          setVehicle(null);
          setLoading(false);
          return;
        }
        dispatch(getVehicleByID(vehicleID))
          .then((vehicleData) => {
            setVehicle((vehicleData && vehicleData[0]) || null);
            setLoading(false);
          })
          .catch(() => {
            setVehicle(null);
            setLoading(false);
          });
      }).catch(() => {
        setLoading(false);
      });
    };
    fetchData();
  }, [params.jobId]);

  const goBack = () => {
    navigate('/app/jobs');
  };

  const killOrder = () => {
    dispatch(killJobById(params.jobId)).then((response) => {
      console.log(response);
    });
    navigate('/app/jobs');
  };
  const killOrderWithFee = () => {
    dispatch(killJobWithFeeById(params.jobId)).then((response) => {
      console.log(response);
    });
    // navigate('/app/jobs');
  };

  const goToServiceDetail = () => {
    navigate(`/app/services/edit/${job && job.order[0].service_id}`, {
      state: job && job.order[0].service_id,
    });
  };

  const goToClientDetail = () => {
    if (job) {
      const clientID = job.order[0].client_id;

      navigate(`/app/clients/edit/${clientID}`, {
        state: clientID,
      });
    }
  };

  const goToWasherDetail = () => {
    if (job && job.order[0].washer_id) {
      const washerID = job.order[0].washer_id;
      navigate(`/app/washers/edit/${washerID}`, {
        state: washerID,
      });
    }
  };

  const order = job && job.order ? job.order[0] : null;

  const formatPrice = (value) => {
    const numericValue = Number(value || 0);
    return `${Number.isFinite(numericValue) ? numericValue.toFixed(2) : '0.00'}€`;
  };

  const renderInvoiceLink = (invoiceUrl) => {
    if (!invoiceUrl) {
      return '-';
    }

    return (
      <a href={invoiceUrl} target="_blank" rel="noreferrer">
        INVOICE
      </a>
    );
  };

  const extraRows =
    order && Number(order.extra_price || 0) > 0
      ? [
          {
            description: 'Extra payment',
            price: Number(order.extra_price || 0),
            observations: order.extra_obs || '-',
            invoiceUrl: order.invoice_url,
          },
        ]
      : [];

  const extraTotal = extraRows.reduce((sum, extraRow) => sum + extraRow.price, 0);
  const showExtrasSection =
    !!order &&
    (extraRows.length > 0 ||
      Number(order.tip || 0) > 0 ||
      Number(order.cancel_price || 0) > 0);

  const discountRedemptions = (job && job.discount_redemptions) || [];
  const fleetDiscount = job && job.fleet_discount;
  const fleetApplied = !!(fleetDiscount && fleetDiscount.applied_to_order);
  const originalPrice = Number((order && order.original_price) || 0);
  const finalPrice = Number((order && order.service_price) || 0);
  const totalSavings = originalPrice > 0 && originalPrice > finalPrice
    ? originalPrice - finalPrice
    : 0;
  const totalSavingsPercent = originalPrice > 0 && totalSavings > 0
    ? Math.round((totalSavings / originalPrice) * 1000) / 10
    : 0;
  const showDiscountsSection =
    !!order && (discountRedemptions.length > 0 || fleetApplied || totalSavings > 0);

  // no_washer demand stubs (and any incomplete legacy rows) have null
  // service/washer/vehicle/image fields. The rest of the form drills into
  // job.client_image[0].url_image, job.washer_image[0].url_image, etc — all
  // of which crash for these rows. Render a small placeholder so the BO
  // at least surfaces what we know instead of going blank.
  const isStub =
    !loading &&
    (!order ||
      order.status_order === 'no_washer' ||
      !job?.client_image?.length ||
      !job?.washer_image?.length ||
      !job?.client_vehicle_image?.length);

  if (isStub) {
    return (
      <Container maxWidth={false}>
        <Box paddingTop={3}>
          <Card>
            <CardHeader title="Job Information" />
          </Card>
        </Box>
        <Box paddingTop={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {!order
                  ? 'Job could not be loaded.'
                  : order.status_order === 'no_washer'
                  ? 'No washer was available when this order was created.'
                  : 'This order has no washer / vehicle / images attached.'}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Booking No: {(order && order.order_id) || params.jobId}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Status: {(order && order.status_order) || '-'}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Created: {(order && order.creation_date) || '-'}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Client: {(order && order.client_firstname) || ''} {(order && order.client_lastname) || ''} ({(order && order.client_email) || '-'})
              </Typography>
              {order && order.client_phonenumber && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Phone: {order.client_phonenumber}
                </Typography>
              )}
              {order && order.address && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Address: {order.address}
                </Typography>
              )}
              {order && order.lat && order.long && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Location: {order.lat}, {order.long}
                  {' '}
                  (<a href={`https://www.google.com/maps?q=${order.lat},${order.long}`} target="_blank" rel="noreferrer">map</a>)
                </Typography>
              )}
              {order && order.service_name && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Service: {order.service_name}
                  {order.service_description ? ` — ${order.service_description}` : ''}
                </Typography>
              )}
              {order && (order.service_price != null || order.original_price != null) && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Price: {formatPrice(order.service_price)}
                  {Number(order.original_price || 0) > Number(order.service_price || 0)
                    ? ` (original ${formatPrice(order.original_price)})`
                    : ''}
                </Typography>
              )}
              {order && order.license_plate && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Vehicle: {order.license_plate}
                  {order.description ? ` — ${order.description}` : ''}
                  {order.colour ? ` (${order.colour})` : ''}
                  {order.class ? ` [${order.class}]` : ''}
                </Typography>
              )}
              {order && order.washer_firstname && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Washer: {order.washer_firstname} {order.washer_lastname || ''} ({order.washer_email || '-'})
                </Typography>
              )}
              {order && order.payment_status && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Payment: {order.payment_status}
                  {order.invoice_url ? ' — ' : ''}
                  {order.invoice_url && (
                    <a href={order.invoice_url} target="_blank" rel="noreferrer">invoice</a>
                  )}
                </Typography>
              )}
              {order && order.complaint && (
                <Typography variant="body2" color="error" gutterBottom>
                  Complaint: {order.complaint}
                </Typography>
              )}
              <Box paddingTop={2}>
                <Button onClick={goBack} variant="contained">Back to jobs</Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
        {/* Chat log card — show even on stub orders */}
        <Box paddingTop={3}>
          <Card>
            <CardHeader title="Chat Log" />
            <CardContent>
              {messagesLoading && <CircularProgress size={20} />}
              {!messagesLoading && messagesError && (
                <Typography variant="body2" color="error">{messagesError}</Typography>
              )}
              {!messagesLoading && !messagesError && chatMessages.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  No in-app chat messages were exchanged on this order.
                </Typography>
              )}
              {!messagesLoading && chatMessages.length > 0 && (
                <Box>
                  {chatMessages.map((m, idx) => (
                    <Box key={m.id || idx} paddingY={0.5}>
                      <Typography variant="caption" color="textSecondary">
                        {m.sender_role || m.sender_id || '?'} — {m.created_at || ''}
                      </Typography>
                      <Typography variant="body2">{m.text || m.message || ''}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth={false}>
        <Box paddingTop={3}>
          <Card>
            <CardHeader title="Job Information" />
          </Card>
        </Box>
        <Box paddingTop={3}>
          <Card>
            {loading && <CircularProgress />}
            {!loading && (
              <CardContent>
                <Grid
                  container
                  spacing={2}
                  justifyContent="flex-start"
                  paddingBottom={5}
                >
                  <Grid item xs={12} sm={6} md={2}>
                    <Card>
                      <CardContent>
                        <Typography>
                          Booking No: {job && job.order[0].order_id}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Card>
                      <CardContent>
                        <Typography>
                          Status: {job && job.order[0].status_order}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Card>
                      <CardContent>
                        <Typography>
                          {job && job.order[0].creation_date}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Card>
                      <CardContent>
                        <Typography>{job && job.order[0].address}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Card>
                      <CardContent>
                        <Typography>
                          Tip: {formatPrice(order && order.tip)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Card>
                      <CardContent>
                        <Typography>
                          Cancel Price: {formatPrice(order && order.cancel_price)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={2}
                  justifyContent="flex-start"
                  paddingBottom={5}
                >
                  <Grid item xs={12} sm={12} md={4}>
                    <Card className={classes.root}>
                      <Avatar
                        alt="Remy Sharp"
                        src={job && job.order[0].service_image}
                        className={classes.large}
                        style={{ borderRadius: 0 }}
                      />
                      <div className={classes.details}>
                        <CardContent className={classes.content}>
                          <Typography component="h5" variant="h5">
                            Service
                          </Typography>
                          <Typography variant="subtitle1" color="textSecondary">
                            {job && job.order[0].service_name}
                          </Typography>
                          <Typography variant="subtitle1" color="textSecondary">
                            {job && job.order[0].service_description}
                          </Typography>
                          <Typography variant="subtitle1" color="textSecondary">
                            {formatPrice(order && order.service_price)}
                          </Typography>
                          <Button
                            className={classes.detailButton}
                            width={60}
                            onClick={goToServiceDetail}
                            variant="contained"
                            disabled={!job}
                          >
                            Detail
                          </Button>
                        </CardContent>
                      </div>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={12} md={4}>
                    <Card className={classes.root}>
                      <Avatar
                        alt="Remy Sharp"
                        src={job && job.client_image[0].url_image}
                        className={classes.large}
                        style={{ borderRadius: 0 }}
                      />
                      <div className={classes.details}>
                        <CardContent className={classes.content}>
                          <Typography component="h5" variant="h5">
                            Client
                          </Typography>
                          <Typography variant="subtitle1" color="textSecondary">
                            {job && job.order[0].client_firstname}{' '}
                            {job && job.order[0].client_lastname}
                          </Typography>
                          <Typography variant="subtitle1" color="textSecondary">
                            {job && job.order[0].client_email}
                          </Typography>
                          <Typography variant="subtitle1" color="textSecondary">
                            {job && job.order[0].client_phonenumber}
                          </Typography>
                          <Button
                            className={classes.detailButton}
                            width={60}
                            onClick={goToClientDetail}
                            variant="contained"
                            disabled={!job}
                          >
                            Detail
                          </Button>
                        </CardContent>
                      </div>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={12} md={4}>
                    <Card className={classes.root}>
                      <Avatar
                        alt="Remy Sharp"
                        src={job && job.washer_image[0].url_image}
                        className={classes.large}
                        style={{ borderRadius: 0 }}
                      />
                      <div className={classes.details}>
                        <CardContent className={classes.content}>
                          <Typography component="h5" variant="h5">
                            Washer
                          </Typography>
                          <Typography variant="subtitle1" color="textSecondary">
                            {job && job.order[0].washer_firstname}{' '}
                            {job && job.order[0].washer_lastname}
                          </Typography>
                          <Typography variant="subtitle1" color="textSecondary">
                            {job && job.order[0].washer_email}
                          </Typography>
                          <Typography variant="subtitle1" color="textSecondary">
                            {job && job.order[0].washer_phonenumber}
                          </Typography>
                          <Button
                            className={classes.detailButton}
                            width={60}
                            onClick={goToWasherDetail}
                            variant="contained"
                            disabled={!job}
                          >
                            Detail
                          </Button>
                        </CardContent>
                      </div>
                    </Card>
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={2}
                  justifyContent="flex-start"
                  paddingBottom={5}
                >
                  <Grid item xs={12} sm={12} md={6}>
                    <Card className={classes.root} style={{ height: 180 }}>
                      <Avatar
                        alt="Remy Sharp"
                        src={job && job.client_vehicle_image[0].url_image}
                        className={classes.large}
                        style={{ borderRadius: 0 }}
                      />
                      <div className={classes.details}>
                        <CardContent className={classes.content}>
                          <Typography component="h5" variant="h5">
                            Vehicle Information
                          </Typography>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>License Plate</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Colour</TableCell>
                                <TableCell>Class</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow hover>
                                <TableCell>
                                  {vehicle && vehicle.license_plate}
                                </TableCell>
                                <TableCell>
                                  {vehicle && vehicle.description}
                                </TableCell>
                                <TableCell>
                                  {vehicle && vehicle.colour}
                                </TableCell>
                                <TableCell>
                                  {vehicle && vehicle.class}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </CardContent>
                      </div>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <Card className={classes.root} style={{ height: 180 }}>
                      <div className={classes.details}>
                        <CardContent className={classes.content}>
                          <Typography component="h5" variant="h5">
                            Invoice Information
                          </Typography>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Price</TableCell>
                                <TableCell>Washer Commission</TableCell>
                                <TableCell>Payment Status</TableCell>
                                <TableCell>Invoice</TableCell>
                                <TableCell>Type</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow hover>
                                <TableCell>
                                  {formatPrice(order && order.service_price)}
                                </TableCell>
                                <TableCell>
                                  {formatPrice(order && order.washer_commission)}
                                </TableCell>
                                <TableCell>
                                  {order && order.payment_status}
                                </TableCell>
                                <TableCell>
                                  {renderInvoiceLink(order && order.invoice_url)}
                                </TableCell>
                                <TableCell>Wash Service</TableCell>
                              </TableRow>
                              {order &&
                                (Number(order.tip || 0) > 0 || order.tip_invoice_url) && (
                                  <TableRow hover>
                                    <TableCell>
                                      {formatPrice(order.tip)}
                                    </TableCell>
                                    <TableCell>-</TableCell>
                                    <TableCell>{order.tip_status || '-'}</TableCell>
                                    <TableCell>
                                      {renderInvoiceLink(order.tip_invoice_url)}
                                    </TableCell>
                                    <TableCell>Tip</TableCell>
                                  </TableRow>
                                )}
                              {order && Number(order.cancel_price || 0) > 0 && (
                                <TableRow hover>
                                  <TableCell>
                                    {formatPrice(order.cancel_price)}
                                  </TableCell>
                                  <TableCell>-</TableCell>
                                  <TableCell>{order.payment_status || '-'}</TableCell>
                                  <TableCell>
                                    {renderInvoiceLink(order.invoice_url)}
                                  </TableCell>
                                  <TableCell>Cancel Fee</TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </div>
                    </Card>
                  </Grid>
                </Grid>
                {showDiscountsSection && (
                  <Grid
                    container
                    spacing={2}
                    justifyContent="flex-start"
                    paddingBottom={5}
                  >
                    <Grid item xs={12}>
                      <Card>
                        <CardHeader
                          title={
                            totalSavings > 0
                              ? `Discounts (saved ${formatPrice(totalSavings)} · ${totalSavingsPercent}% off)`
                              : 'Discounts'
                          }
                        />
                        <CardContent>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Source</TableCell>
                                <TableCell>Detail</TableCell>
                                <TableCell>Percent</TableCell>
                                <TableCell>Applied</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {originalPrice > 0 && (
                                <TableRow hover>
                                  <TableCell>Original price</TableCell>
                                  <TableCell>
                                    {formatPrice(originalPrice)} → {formatPrice(finalPrice)}
                                  </TableCell>
                                  <TableCell>-</TableCell>
                                  <TableCell>-</TableCell>
                                </TableRow>
                              )}
                              {discountRedemptions.map((r) => (
                                <TableRow hover key={r.redemption_id}>
                                  <TableCell>Discount code</TableCell>
                                  <TableCell>{r.code || '-'}</TableCell>
                                  <TableCell>
                                    {r.discount_percent != null
                                      ? `${r.discount_percent}%`
                                      : '-'}
                                  </TableCell>
                                  <TableCell>
                                    {r.used_at ? 'Yes' : 'Redeemed'}
                                  </TableCell>
                                </TableRow>
                              ))}
                              {fleetDiscount && (
                                <TableRow hover>
                                  <TableCell>Fleet</TableCell>
                                  <TableCell>
                                    {fleetDiscount.company_name} (VAT {fleetDiscount.company_vat})
                                  </TableCell>
                                  <TableCell>{fleetDiscount.discount_percent}%</TableCell>
                                  <TableCell>
                                    {fleetApplied
                                      ? 'Yes'
                                      : 'Available (personal profile used)'}
                                  </TableCell>
                                </TableRow>
                              )}
                              {discountRedemptions.length === 0 && !fleetDiscount && totalSavings > 0 && (
                                <TableRow hover>
                                  <TableCell colSpan={4}>
                                    Discount of {formatPrice(totalSavings)} applied (source not tracked).
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
                {showExtrasSection && (
                  <Grid
                    container
                    spacing={2}
                    justifyContent="flex-start"
                    paddingBottom={5}
                  >
                    <Grid item xs={12}>
                      <Card>
                        <CardHeader
                          title={`Extras (${formatPrice(extraTotal)} total)`}
                        />
                        <CardContent>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Description</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Observations</TableCell>
                                <TableCell>Invoice</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {extraRows.length > 0 ? (
                                extraRows.map((extraRow) => (
                                  <TableRow hover key={extraRow.description}>
                                    <TableCell>{extraRow.description}</TableCell>
                                    <TableCell>
                                      {formatPrice(extraRow.price)}
                                    </TableCell>
                                    <TableCell>
                                      {extraRow.observations}
                                    </TableCell>
                                    <TableCell>
                                      {renderInvoiceLink(extraRow.invoiceUrl)}
                                    </TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow hover>
                                  <TableCell colSpan={4}>No extras</TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                          {(Number((order && order.tip) || 0) > 0 ||
                            Number((order && order.cancel_price) || 0) > 0) && (
                            <Box pt={3}>
                              <Table>
                                <TableBody>
                                  {(Number((order && order.tip) || 0) > 0 ||
                                    (order && order.tip_invoice_url)) && (
                                    <TableRow hover>
                                      <TableCell>Tip invoice</TableCell>
                                      <TableCell>
                                        {formatPrice(order && order.tip)}
                                      </TableCell>
                                      <TableCell>
                                        {renderInvoiceLink(
                                          order && order.tip_invoice_url
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  )}
                                  {Number((order && order.cancel_price) || 0) >
                                    0 && (
                                    <TableRow hover>
                                      <TableCell>Cancel fee invoice</TableCell>
                                      <TableCell>
                                        {formatPrice(order && order.cancel_price)}
                                      </TableCell>
                                      <TableCell>
                                        {renderInvoiceLink(order && order.invoice_url)}
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
                <Grid
                  container
                  spacing={2}
                  justifyContent="flex-start"
                  paddingBottom={5}
                >
                  <Grid item xs={12} sm={12} md={6}>
                    <Typography component="h5" variant="h5">
                      Before Images
                    </Typography>
                    <Card className={classes.root}>
                      <div
                        className={classes.details}
                        style={{ height: 400, overflowY: 'auto' }}
                      >
                        <CardContent className={classes.content}>
                          <SingleLineImageList
                            itemData={job && job.order_before_image}
                          />
                        </CardContent>
                      </div>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <Typography component="h5" variant="h5">
                      After Images
                    </Typography>
                    <Card className={classes.root}>
                      <div
                        className={classes.details}
                        style={{ height: 400, overflowY: 'auto' }}
                      >
                        <CardContent className={classes.content}>
                          <SingleLineImageList
                            itemData={job && job.order_after_image}
                          />
                        </CardContent>
                      </div>
                    </Card>
                  </Grid>
                </Grid>
                <div>
                  <Button
                    color="primary"
                    size="large"
                    width={60}
                    onClick={goBack}
                    variant="contained"
                  >
                    GO BACK
                  </Button>
                  {job &&
                  job.order[0] &&
                  (job.order[0].status_order === 'payment' ||
                    job.order[0].status_order === 'searching' ||
                    job.order[0].status_order === 'ongoing') ? (
                    <span>
                      <Button
                        color="error"
                        size="large"
                        width={60}
                        onClick={killOrder}
                        variant="contained"
                        style={{
                          float:
                            'right',
                        }}
                      >
                        KILL ORDER NO FEE
                      </Button>
                      <Button
                        color="error"
                        size="large"
                        width={60}
                        onClick={killOrderWithFee}
                        variant="contained"
                        style={{ float: 'right', margin: '0 10px' }}
                      >
                        KILL ORDER WITH FEE
                      </Button>
                    </span>
                  ) : (
                    ''
                  )}
                </div>
              </CardContent>
            )}
          </Card>

          {/* Plan v4.7: chat messages (Firestore) + system notifications */}
          <Card style={{ marginTop: 16 }}>
            <CardContent>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Chat messages</h3>
                <Button size="small" onClick={loadMessages} disabled={messagesLoading}>
                  {messagesLoading ? 'Loading…' : 'Refresh'}
                </Button>
              </div>
              {messagesError && (<p style={{ color: '#c00' }}>⚠ {messagesError}</p>)}

              {!messagesLoading && chatMessages.length === 0 && !messagesError && (
                <p style={{ color: '#888' }}>No chat messages exchanged.</p>
              )}
              {chatMessages.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 0' }}>
                  {chatMessages.map((m, i) => (
                    <div key={i} style={{
                      alignSelf: m.isWasher ? 'flex-start' : 'flex-end',
                      maxWidth: '70%',
                      background: m.isWasher ? '#f0f0f0' : '#34cadc',
                      color: m.isWasher ? '#222' : 'white',
                      padding: '8px 12px',
                      borderRadius: 14,
                      fontSize: 14,
                    }}>
                      <div>{m.content}</div>
                      <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4, textAlign: 'right' }}>
                        {m.isWasher ? '🧽 washer' : '👤 client'} · {m.createdAt ? new Date(Number(m.createdAt)).toLocaleString('pt-PT') : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <details style={{ marginTop: 16 }}>
                <summary style={{ cursor: 'pointer', color: '#666' }}>System notifications ({messages.length})</summary>
                {messages.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
                    <thead>
                      <tr style={{ background: '#f7f7f7' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px' }}>When</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px' }}>Direction</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px' }}>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {messages.map((m) => (
                        <tr key={m.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                            {new Date(m.creation_date).toLocaleString('pt-PT')}
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            {m.direction === 'washer_to_client' ? 'washer→client' : 'client→washer'}
                          </td>
                          <td style={{ padding: '6px 8px' }}>{m.description || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </details>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </>
  );
};

export default EditJob;
